import { supabase } from './supabase'

const GCS_BUCKET_URL = 'https://storage.googleapis.com/trainingmatrix'
const SUPABASE_STORAGE_URL = 'https://yqmlqctpakqxtwzrdofl.supabase.co/storage/v1'

/**
 * Detect URL type: 'gcs' | 'supabase' | 'path' | 'other'
 */
export function detectUrlType(url) {
  if (!url) return 'other'
  if (url.startsWith(GCS_BUCKET_URL) || url.startsWith('https://storage.googleapis.com/')) return 'gcs'
  if (url.startsWith(SUPABASE_STORAGE_URL) || url.startsWith('https://yqmlqctpakqxtwzrdofl.supabase.co/storage/')) return 'supabase'
  if (!url.startsWith('http://') && !url.startsWith('https://')) return 'path'
  return 'other'
}

/**
 * Construct a public GCS URL from a file path (no API call).
 */
export function gcsGetPublicUrl(filePath) {
  return `${GCS_BUCKET_URL}/${filePath}`
}

/**
 * Upload a file to GCS via the generate-upload-url edge function.
 * Returns { publicUrl, filePath }
 */
export async function gcsUploadFile(file, { folderPath, fileName, contentType, signal }) {
  const safeName = fileName || file.name
  const safeContentType = contentType || file.type || 'application/octet-stream'

  signal?.throwIfAborted()

  const { data: urlData, error: urlError } = await supabase.functions.invoke(
    'generate-upload-url',
    {
      body: {
        fileName: safeName,
        contentType: safeContentType,
        folderPath,
      },
    }
  )

  if (urlError || !urlData?.signedUrl) {
    throw new Error(urlError?.message || 'Failed to get upload URL')
  }

  signal?.throwIfAborted()

  const uploadRes = await fetch(urlData.signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': safeContentType },
    body: file,
    signal,
  })

  if (!uploadRes.ok) {
    throw new Error(`Upload failed with status ${uploadRes.status}`)
  }

  return {
    publicUrl: urlData.publicUrl,
    filePath: urlData.fileName,
  }
}

/**
 * Delete a file from GCS.
 */
export async function gcsDeleteFile(filePath) {
  const { error } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'delete', filePath },
  })
  if (error) throw new Error(error.message || 'Failed to delete file')
}

/**
 * Get a signed read URL (inline viewing / doc parser — no content-disposition).
 * 1-hour expiry.
 */
export async function gcsGetSignedReadUrl(filePath) {
  const { data, error } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'read', filePath },
  })
  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'Failed to get signed read URL')
  }
  return data.signedUrl
}

/**
 * Get a signed download URL (forces browser download via content-disposition).
 * Returns { signedUrl, fileName }
 */
export async function gcsGetSignedDownloadUrl(filePath) {
  const { data, error } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'download', filePath },
  })
  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'Failed to get signed download URL')
  }
  return { signedUrl: data.signedUrl, fileName: data.fileName }
}

/**
 * Server-side copy within GCS bucket.
 * Returns { publicUrl, filePath }
 */
export async function gcsCopyFile(sourcePath, destinationPath) {
  const { data, error } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'copy', filePath: sourcePath, destinationPath },
  })
  if (error || !data?.success) {
    throw new Error(error?.message || 'Failed to copy file')
  }
  return { publicUrl: data.publicUrl, filePath: data.filePath }
}

/**
 * Extract the GCS file path from a full GCS URL.
 * If already a path (not a URL), returns as-is.
 */
export function extractGcsPath(urlOrPath) {
  if (!urlOrPath) return null
  const bucketPrefix = `${GCS_BUCKET_URL}/`
  if (urlOrPath.startsWith(bucketPrefix)) {
    return urlOrPath.slice(bucketPrefix.length)
  }
  return urlOrPath
}
